import { jsx as _jsx } from "react/jsx-runtime";
import { uploadByIdSelector } from "@air/redux-uploader";
import { useDndContext } from "@dnd-kit/core";
import { useCallback } from "react";
import { KanbanColumnLoadMoreItems } from "~/components/KanbanView/KanbanColumnsManagers/shared/KanbanColumnItems/KanbanColumnLoadMoreItems";
import { KANBAN_ASSET_CARD_HEIGHT, KANBAN_BOARD_CARD_HEIGHT, KANBAN_FILE_CARD_HEIGHT, KANBAN_ITEM_DROP_PLACEHOLDER_HEIGHT, KANBAN_VIRTUALIZED_CARD_GUTTER } from "~/components/KanbanView/shared/kanbanConstants";
import { ASSET_CARD_ROW_MT } from "~/constants/assetCard";
import { WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT } from "~/constants/WorkspaceSpacing";
import { visibleCustomFieldsLengthSelector } from "~/store/configViews/selectors";
import { kanbanColumnItemsSelector } from "~/store/kanbanManager/selectors";
import { DndItemType, isDndSortableKanbanItemData } from "~/types/DndKit";
import { useAirStore } from "~/utils/ReduxUtils";
import { KanbanItem } from "./KanbanItem/KanbanItem";
export const useRenderKanbanItem = (param)=>{
    let { kanbanColumnId, isFetchingNextPage, fetchNextPage } = param;
    const { active, over } = useDndContext();
    const activeData = active === null || active === void 0 ? void 0 : active.data.current;
    const overData = over === null || over === void 0 ? void 0 : over.data.current;
    const { getState } = useAirStore();
    const getInitialItemCardHeight = useCallback((itemData)=>{
        let size = 0;
        const visibleCustomFieldsLength = visibleCustomFieldsLengthSelector(getState());
        switch(itemData.dndType){
            case DndItemType.kanbanBoard:
                size = KANBAN_BOARD_CARD_HEIGHT;
                break;
            case DndItemType.kanbanAsset:
                {
                    const addedMargin = visibleCustomFieldsLength ? ASSET_CARD_ROW_MT : 0;
                    size = KANBAN_ASSET_CARD_HEIGHT + addedMargin + visibleCustomFieldsLength * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
                    break;
                }
            case DndItemType.kanbanFile:
                {
                    const additionalRows = Math.max(0, visibleCustomFieldsLength - 2);
                    size = KANBAN_FILE_CARD_HEIGHT + additionalRows * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
                    break;
                }
            case DndItemType.kanbanUpload:
                {
                    const apiData = uploadByIdSelector(getState(), itemData.itemId);
                    if (apiData === null || apiData === void 0 ? void 0 : apiData.isNonMedia) {
                        size = KANBAN_FILE_CARD_HEIGHT;
                    } else {
                        size = KANBAN_ASSET_CARD_HEIGHT;
                    }
                    break;
                }
        }
        return size;
    }, [
        getState
    ]);
    const renderKanbanItem = useCallback((param)=>{
        let { index, style } = param;
        const kanbanDndItems = kanbanColumnItemsSelector(getState(), kanbanColumnId);
        if (index === kanbanDndItems.length) {
            return /*#__PURE__*/ _jsx("div", {
                style: style,
                children: /*#__PURE__*/ _jsx(KanbanColumnLoadMoreItems, {
                    isLoading: isFetchingNextPage,
                    loadMore: fetchNextPage
                })
            });
        }
        const kanbanDndItem = kanbanDndItems[index] || null;
        if (!kanbanDndItem) return null;
        const height = getInitialItemCardHeight(kanbanDndItem);
        return /*#__PURE__*/ _jsx(KanbanItem, {
            cardHeight: height,
            kanbanDndItemData: kanbanDndItem,
            style: style
        });
    }, [
        fetchNextPage,
        getInitialItemCardHeight,
        getState,
        isFetchingNextPage,
        kanbanColumnId
    ]);
    const getItemKey = useCallback((index)=>{
        const kanbanItems = kanbanColumnItemsSelector(getState(), kanbanColumnId);
        const kanbanItem = kanbanItems[index] || null;
        return (kanbanItem === null || kanbanItem === void 0 ? void 0 : kanbanItem.sortableKanbanItemKey) || String(index);
    }, [
        getState,
        kanbanColumnId
    ]);
    const getVirtualizedItemSize = useCallback((index)=>{
        const kanbanItems = kanbanColumnItemsSelector(getState(), kanbanColumnId);
        const itemData = kanbanItems[index] || null;
        if (!itemData) return 0;
        const height = getInitialItemCardHeight(itemData);
        let addedHeightForBlueLine = 0;
        if (isDndSortableKanbanItemData(activeData) && !!(overData === null || overData === void 0 ? void 0 : overData.itemId) && overData.itemId === itemData.itemId && activeData.itemId !== overData.itemId) addedHeightForBlueLine = KANBAN_ITEM_DROP_PLACEHOLDER_HEIGHT;
        return height + addedHeightForBlueLine + KANBAN_VIRTUALIZED_CARD_GUTTER;
    }, [
        getInitialItemCardHeight,
        activeData,
        overData,
        getState,
        kanbanColumnId
    ]);
    return {
        renderKanbanItem,
        getItemKey,
        getItemSize: getVirtualizedItemSize
    };
};
